import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { hardwareMicDeviceKey, hardwareMicDevicesAtom } from '@heidi/dictate/data';
import { sanitizeError } from '@heidi/runtime/logger';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import {
  type HardwareMicConnectedPayload,
  type HardwareMicDevice,
  listHardwareMics,
  listenHardwareMicConnected,
  listenHardwareMicDisconnected,
} from './api';

/**
 * Mirror the HID reader's connected-device snapshot into `hardwareMicDevicesAtom`.
 * Rust emits only on transitions, so the atom updates only on plug/unplug —
 * never on the reader's 2s refresh tick.
 */
export const useHardwareMicDevicesSync = (enabled: boolean): void => {
  const setHardwareMicDevices = useSetAtom(hardwareMicDevicesAtom);

  useEffect(() => {
    if (!isTauri()) return;

    if (!enabled) {
      setHardwareMicDevices([]);
      return;
    }

    let cancelled = false;
    const unlisteners: (() => void)[] = [];
    // Connects that land while the seed list is in flight would be lost to
    // the stale snapshot: Rust emits only on transitions, so a device that
    // plugged in mid-flight never re-emits. Track their keys from the moment
    // the list is requested and keep them on top of the seed.
    const connectedWhileListing = new Set<string>();
    let listing = false;
    const upsert = ({
      model,
      product,
      serial,
      vid,
      pid,
    }: HardwareMicConnectedPayload) => {
      const d: HardwareMicDevice = { model, product, serial, vid, pid };
      if (listing) connectedWhileListing.add(hardwareMicDeviceKey(d));
      setHardwareMicDevices((prev) =>
        prev.some((p) => hardwareMicDeviceKey(p) === hardwareMicDeviceKey(d))
          ? prev
          : [...prev, d]
      );
    };
    const remove = (d: HardwareMicDevice) =>
      setHardwareMicDevices((prev) =>
        prev.filter((p) => hardwareMicDeviceKey(p) !== hardwareMicDeviceKey(d))
      );
    // Store each unlisten the moment its listen resolves, so a later
    // registration failure can never leak an earlier listener. Returns false
    // when the effect already tore down, so the setup stops there.
    const register = async <P>(
      listen: (cb: (event: { payload: P }) => void) => Promise<() => void>,
      onDevice: (d: P) => void
    ): Promise<boolean> => {
      const un = await listen((event) => onDevice(event.payload));
      if (cancelled) {
        un();
        return false;
      }
      unlisteners.push(un);
      return true;
    };

    void (async () => {
      try {
        // Subscribe before seeding so a connect between the two calls is not
        // lost; the seed is the authoritative snapshot with those connects
        // kept on top. A registration landing after teardown ends the setup
        // early — no further IPC on a dead effect.
        if (!(await register(listenHardwareMicConnected, upsert))) return;
        if (!(await register(listenHardwareMicDisconnected, remove))) return;
        listing = true;
        const devices = await listHardwareMics();
        if (!cancelled) {
          setHardwareMicDevices((prev) => {
            const seeded = new Set(devices.map(hardwareMicDeviceKey));
            return [
              ...devices,
              ...prev.filter(
                (p) =>
                  connectedWhileListing.has(hardwareMicDeviceKey(p)) &&
                  !seeded.has(hardwareMicDeviceKey(p))
              ),
            ];
          });
        }
        listing = false;
      } catch (err: unknown) {
        // Fail closed: a partial registration tears down what did register.
        for (const un of unlisteners.splice(0)) un();
        logger.error({
          event_type: 'DICTATION_HARDWARE_MIC_DEVICES_SYNC_FAILED',
          details: { error: sanitizeError(err) },
        });
      }
    })();

    return () => {
      cancelled = true;
      unlisteners.forEach((u) => u());
    };
  }, [enabled, setHardwareMicDevices]);
};
